import { useKeyPress, useReactFlow } from '@xyflow/react';
import { t } from 'i18next';
import {
  Fullscreen,
  Hand,
  Minus,
  MousePointer,
  Plus,
  RotateCw,
} from 'lucide-react';
import { AutoAlignIcon } from '@/assets/img/custom/autoAlign';
import { useCallback } from 'react';

import { useBuilderStateContext } from '../builder-hooks';

import { CANVAS_PARAMETERS } from './utils/consts';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { flowCanvasUtils } from './utils/flow-canvas-utils';

const PanningModeIndicator = ({ toggled }: { toggled: boolean }) => {
  return (
    <div
      className={cn(
        'absolute transition-all bg-primary/15 w-full h-full top-0 left-0',
        {
          'opacity-0': !toggled,
        }
      )}
    ></div>
  );
};

const CanvasControls = () => {
  const { zoomIn, zoomOut, zoomTo, fitView } = useReactFlow();
  const alignAllNodes = flowCanvasUtils.useResetNodesPosition();
  const [flowVersion] = useBuilderStateContext((state) => [state.flowVersion]);

  const handleZoomIn = useCallback(() => {
    zoomIn({
      duration: CANVAS_PARAMETERS.animationDuration,
    });
  }, [zoomIn]);

  const handleZoomOut = useCallback(() => {
    zoomOut({
      duration: CANVAS_PARAMETERS.animationDuration,
    });
  }, [zoomOut]);

  const handleZoomReset = useCallback(() => {
    zoomTo(1, { duration: CANVAS_PARAMETERS.animationDuration });
  }, [zoomTo]);

  const handleFitView = useCallback(() => {
    fitView({ duration: CANVAS_PARAMETERS.animationDuration });
  }, [fitView]);

  const handleAutoAlign = useCallback(() => {
    const flowStorageKey = `flow-positions-${flowVersion.id}`;
    localStorage.removeItem(flowStorageKey);

    alignAllNodes();

    setTimeout(() => {
      window.dispatchEvent(new Event('flow-canvas-force-reset'));
    }, 50);

    setTimeout(() => {
      handleFitView();
    }, 150);
  }, [alignAllNodes, handleFitView, flowVersion.id]);

  const [setPanningMode, panningMode] = useBuilderStateContext((state) => {
    return [state.setPanningMode, state.panningMode];
  });
  const spacePressed = useKeyPress('Space');
  const shiftPressed = useKeyPress('Shift');
  const isInGrabMode =
    (spacePressed || panningMode === 'grab') && !shiftPressed;

  return (
    <>
      <div className="bg-secondary absolute left-[10px] bottom-[60px] z-50 flex flex-col gap-2 shadow-md">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                if (!spacePressed) {
                  setPanningMode('pan');
                }
              }}
              className="relative focus:outline-0"
            >
              <PanningModeIndicator toggled={!isInGrabMode} />
              <MousePointer className="w-5 h-5"></MousePointer>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">{t('Select Mode')}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                if (!spacePressed) {
                  setPanningMode('grab');
                }
              }}
              className="relative focus:outline-0"
            >
              <PanningModeIndicator toggled={isInGrabMode} />

              <Hand className="w-5 h-5"></Hand>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">{t('Move Mode')}</TooltipContent>
        </Tooltip>
      </div>
      <div className="bg-secondary absolute left-[10px] bottom-[10px] z-50 flex flex-row shadow-md">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="secondary" size="sm" onClick={handleZoomReset}>
              <RotateCw className="w-5 h-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">{t('Reset Zoom')}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="secondary" size="sm" onClick={handleZoomIn}>
              <Plus className="w-5 h-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">{t('Zoom In')}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="secondary" size="sm" onClick={handleZoomOut}>
              <Minus className="w-5 h-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">{t('Zoom Out')}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="secondary" size="sm" onClick={handleFitView}>
              <Fullscreen className="w-5 h-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">{t('Fit to View')}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="secondary" size="sm" onClick={handleAutoAlign}>
              <AutoAlignIcon className="w-5 h-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">{t('Auto-align')}</TooltipContent>
        </Tooltip>
      </div>
    </>
  );
};

export { CanvasControls };
